<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";
import {
  beginConditionalVisibilityReset,
  completeConditionalVisibilityReset,
  conditionalVisibilityStateLocator,
  conditionalVisibilityStateKey,
  getPendingConditionalVisibilityReset,
  normalizeConditionalVisibilityState,
  updateConditionalVisibilityState,
  type ConditionalVisibilityState,
} from "#bb/lib/conditionalVisibilityState";
import {
  normalizeConditionalVisibilityPersistenceFailureBehavior,
  shouldShowContentOnVisibilityPersistenceFailure,
} from "#bb/lib/conditionalVisibilityPersistence";
import {
  createResetTimerState,
  restoreTimerStateForDefinition,
} from "#bb/lib/timer";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { selectConditionalVisibilityMessage } from "#bb/lib/conditionalVisibilityMessages";
import {
  createVisibilityMountState,
  evaluateVisibilityForMount,
  normalizeVisibilityPolicy,
  reconcileVisibilityHistory,
  recordVisibilityView,
  type VisibilityEligibility,
  type VisibilityHistory,
} from "#bb/lib/visibilityPolicy";

const props = defineProps<{ id: string }>();
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", {
  editor: false,
  resourceKey: "default",
});
const logger = useLogger("ConditionalVisibility");
const userStateStorage = useUserStateStorage();
const timerStateStorage = useTimerStateStorage();
const conditionValues = useConditionValues(features.resourceKey || "default");
const { getAnswer } = useAnswers();

const runtimeState = shallowRef<ConditionalVisibilityState | null>(null);
const history = shallowRef<VisibilityHistory | null>(null);
const evaluation = shallowRef<VisibilityEligibility | null>(null);
const visible = shallowRef(false);
const loading = shallowRef(!features.editor);
const stateReady = shallowRef(features.editor);
const blockingPersistenceError = shallowRef("");
const mountState = shallowRef(createVisibilityMountState(props.id));
const resetBusy = shallowRef(false);
const resetError = shallowRef("");

let persistenceQueue: Promise<unknown> = Promise.resolve();
let consuming = false;
let closingReveal = false;

const policy = computed(() => normalizeVisibilityPolicy(definitionData.value?.policy));
const persistenceFailureBehavior = computed(() =>
  normalizeConditionalVisibilityPersistenceFailureBehavior(
    definitionData.value?.persistenceFailureBehavior,
  ),
);
const showContentOnPersistenceFailure = computed(() =>
  shouldShowContentOnVisibilityPersistenceFailure(
    persistenceFailureBehavior.value,
  ),
);
const hasPersistenceFailure = computed(
  () => Boolean(blockingPersistenceError.value),
);
const configuredTimerId = computed(() =>
  typeof definitionData.value?.timerBlockId === "string"
    ? definitionData.value.timerBlockId.trim()
    : "",
);
const resetEnabled = computed(() => definitionData.value?.resetEnabled === true);
const resetLimit = computed(() => {
  const value = Number(definitionData.value?.resetLimit);
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 1;
});
const remainingResets = computed(() =>
  Math.max(0, resetLimit.value - (runtimeState.value?.resetCount ?? 0)),
);
const resetAvailable = computed(
  () =>
    resetEnabled.value
    && (!configuredTimerId.value || Boolean(selectedTimer.value))
    && (
      remainingResets.value > 0
      || Boolean(runtimeState.value?.pendingResetId)
    )
    && !hasPersistenceFailure.value,
);

function findNestedTimer(nodes: unknown, timerId: string): Record<string, unknown> | null {
  if (!Array.isArray(nodes) || !timerId) return null;
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    const block = node as Record<string, unknown>;
    if (
      block.id === timerId
      && block.componentId === ComponentDefinitionIds.TIMER
    ) {
      return block;
    }
    const nested = findNestedTimer(block.children, timerId);
    if (nested) return nested;
  }
  return null;
}

const selectedTimer = computed(() =>
  findNestedTimer(definitionData.value?.children, configuredTimerId.value),
);

const report = inject<unknown | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const { applyTemplate } = useTemplating(templateContext, { logger });
const hiddenMessageEnabled = computed(
  () => definitionData.value?.hiddenMessageEnabled === true,
);
const hiddenMessage = computed(() =>
  applyTemplate(
    typeof definitionData.value?.hiddenMessage === "string"
      ? definitionData.value.hiddenMessage
      : "This content is no longer available.",
  ),
);
const resetLabel = computed(() =>
  applyTemplate(
    typeof definitionData.value?.resetLabel === "string"
      ? definitionData.value.resetLabel
      : "Request another attempt",
  ),
);
const resetExhaustedMessage = computed(() =>
  applyTemplate(
    typeof definitionData.value?.resetExhaustedMessage === "string"
      ? definitionData.value.resetExhaustedMessage
      : "No resets remain for this content.",
  ),
);
const isResetExhausted = computed(
  () => resetEnabled.value && remainingResets.value === 0,
);
const hasResetExhaustedMessage = computed(
  () => resetExhaustedMessage.value.trim().length > 0,
);
const activeMessageSelection = computed(() =>
  selectConditionalVisibilityMessage({
    hiddenMessageEnabled: hiddenMessageEnabled.value,
    hiddenMessage: hiddenMessage.value,
    resetExhausted: isResetExhausted.value,
    resetExhaustedMessage: resetExhaustedMessage.value,
  }),
);
const showHiddenState = computed(
  () => hiddenMessageEnabled.value || resetEnabled.value || Boolean(resetError.value),
);
const hiddenMessageData = useAsyncData(
  `conditional-visibility-hidden-${props.id}`,
  async () => {
    if (!hiddenMessage.value) return null;
    const { parseMarkdown } = await import("@nuxtjs/mdc/runtime");
    return parseMarkdown(hiddenMessage.value, {
      toc: false,
      contentHeading: false,
    });
  },
  { lazy: import.meta.client, watch: [hiddenMessage] },
);
if (import.meta.server) {
  await hiddenMessageData;
}
const { data: parsedHiddenMessage } = hiddenMessageData;
const resetExhaustedMessageData = useAsyncData(
  `conditional-visibility-reset-exhausted-${props.id}`,
  async () => {
    if (!hasResetExhaustedMessage.value) return null;
    const { parseMarkdown } = await import("@nuxtjs/mdc/runtime");
    return parseMarkdown(resetExhaustedMessage.value, {
      toc: false,
      contentHeading: false,
    });
  },
  {
    lazy: import.meta.client,
    watch: [resetExhaustedMessage, hasResetExhaustedMessage],
  },
);
if (import.meta.server) {
  await resetExhaustedMessageData;
}
const { data: parsedResetExhaustedMessage } = resetExhaustedMessageData;
const activeHiddenMarkdown = computed(() =>
  activeMessageSelection.value?.kind === "reset-exhausted"
    ? parsedResetExhaustedMessage.value
    : activeMessageSelection.value?.kind === "hidden"
      ? parsedHiddenMessage.value
      : null,
);

const authorStatus = computed(() => {
  const conditionCount = policy.value.triggers.length;
  return `${conditionCount} ${conditionCount === 1 ? "view limit" : "view limits"}`;
});

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Conditional visibility",
    description: "Configure visibility limits",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Configure when nested content can be viewed",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Conditional Visibility",
    icon: "lucide:eye",
  },
});

function persistRuntimeState(): Promise<void> {
  if (!runtimeState.value || features.editor) return Promise.resolve();

  const dataPath = getResolvedDataPath(props.id);
  const locator = conditionalVisibilityStateLocator(dataPath, props.id);
  const snapshot = runtimeState.value;
  const operation = persistenceQueue.then(async () => {
    await userStateStorage.save(locator, {
      dataPath,
      key: conditionalVisibilityStateKey(props.id),
      valueType: "json",
      value: snapshot,
      updatedAt: snapshot.updatedAt,
    });
    blockingPersistenceError.value = "";
  });
  persistenceQueue = operation.catch(() => undefined);
  return operation;
}

function handlePersistenceFailure(options: {
  operation: string;
  error: unknown;
  blockingMessage: string;
}): boolean {
  const failOpen = showContentOnPersistenceFailure.value;
  if (failOpen) {
    blockingPersistenceError.value = "";
  } else {
    blockingPersistenceError.value = options.blockingMessage;
  }
  logger.error(`Conditional visibility ${options.operation} failed`, {
    blockId: props.id,
    dataPath: getResolvedDataPath(props.id),
    persistenceFailureBehavior: persistenceFailureBehavior.value,
    error: options.error,
  });
  return failOpen;
}

function updateEvaluation() {
  if (!history.value) return;
  evaluation.value = evaluateVisibilityForMount(
    policy.value,
    history.value,
    mountState.value,
  );
}

function createActiveReveal(timerBlockId: string, nowMs = Date.now()) {
  return {
    id: globalThis.crypto?.randomUUID?.()
      ?? `reveal-${nowMs}-${Math.random()}`,
    timerBlockId,
    startedAt: new Date(nowMs).toISOString(),
  };
}

async function consumeView() {
  if (
    consuming
    || mountState.value.counted
    || !history.value
    || !evaluation.value?.eligible
  ) {
    return;
  }

  consuming = true;
  blockingPersistenceError.value = "";
  const result = recordVisibilityView(
    policy.value,
    history.value,
    mountState.value,
  );

  if (!result.recorded) {
    consuming = false;
    return;
  }

  if (!runtimeState.value) {
    handlePersistenceFailure({
      operation: "view state preparation",
      error: new Error("Conditional visibility state is unavailable."),
      blockingMessage: "This content is temporarily unavailable because its visibility state could not be prepared.",
    });
    if (showContentOnPersistenceFailure.value) {
      history.value = result.history;
      mountState.value = result.mount;
      visible.value = true;
      updateEvaluation();
    }
    consuming = false;
    return;
  }

  const previous = runtimeState.value;
  runtimeState.value = updateConditionalVisibilityState(
    previous,
    {
      history: result.history,
      activeReveal: selectedTimer.value
        ? createActiveReveal(configuredTimerId.value)
        : null,
    },
  );

  try {
    await persistRuntimeState();
  } catch (error) {
    const failOpen = handlePersistenceFailure({
      operation: "view history save",
      error,
      blockingMessage: "This content is temporarily unavailable because its view history could not be saved. Please try again.",
    });
    if (!failOpen) {
      runtimeState.value = previous;
      return;
    }
  } finally {
    consuming = false;
  }

  history.value = result.history;
  mountState.value = result.mount;
  visible.value = true;
  evaluation.value = evaluateVisibilityForMount(
    policy.value,
    history.value,
    mountState.value,
  );
}

async function selectedTimerIsActive(): Promise<boolean> {
  if (!selectedTimer.value) return false;
  const persisted = await timerStateStorage.load(configuredTimerId.value);
  const restored = restoreTimerStateForDefinition(
    persisted,
    selectedTimer.value,
  );
  return restored.status !== "completed";
}

async function closeActiveReveal() {
  if (
    closingReveal
    || resetBusy.value
    || !runtimeState.value?.activeReveal
    || features.editor
  ) {
    return;
  }
  closingReveal = true;
  const previous = runtimeState.value;
  runtimeState.value = updateConditionalVisibilityState(
    previous,
    { activeReveal: null },
  );
  try {
    await persistRuntimeState();
  } catch (error) {
    runtimeState.value = previous;
    const failOpen = handlePersistenceFailure({
      operation: "active reveal closure",
      error,
      blockingMessage: "This content was hidden because its completed view could not be saved safely.",
    });
    visible.value = failOpen;
    updateEvaluation();
    return;
  } finally {
    closingReveal = false;
  }
  visible.value = false;
  updateEvaluation();
}

async function completePendingReset() {
  if (!runtimeState.value) return false;
  const pending = getPendingConditionalVisibilityReset(runtimeState.value);
  if (!pending) return false;
  if (
    pending.timerBlockId
    && (
      pending.timerBlockId !== configuredTimerId.value
      || !selectedTimer.value
      || !pending.replacementTimerState
    )
  ) {
    throw new Error("The pending reset timer no longer matches this container.");
  }

  if (pending.timerBlockId && pending.replacementTimerState) {
    await timerStateStorage.save(
      pending.timerBlockId,
      pending.replacementTimerState,
    );
  }
  const pendingState = runtimeState.value;
  const completedState = completeConditionalVisibilityReset(
    pendingState,
    pending.id,
    policy.value,
  );
  runtimeState.value = completedState;
  try {
    await persistRuntimeState();
  } catch (error) {
    runtimeState.value = pendingState;
    throw error;
  }
  history.value = runtimeState.value.history;
  mountState.value = {
    mountId: pending.id,
    counted: true,
  };
  visible.value = true;
  updateEvaluation();
  return true;
}

async function resetContent() {
  if (
    resetBusy.value
    || !runtimeState.value
    || !resetAvailable.value
  ) {
    return;
  }

  resetBusy.value = true;
  resetError.value = "";
  const previous = runtimeState.value;

  try {
    if (runtimeState.value.pendingResetId) {
      await completePendingReset();
      return;
    }
    const timerId = configuredTimerId.value;
    const nowMs = Date.now();
    const previousTimerState = selectedTimer.value
      ? restoreTimerStateForDefinition(
          await timerStateStorage.load(timerId),
          selectedTimer.value,
          nowMs,
        )
      : null;
    const replacementTimerState = selectedTimer.value
      ? createResetTimerState(selectedTimer.value, nowMs)
      : null;
    const resetId = globalThis.crypto?.randomUUID?.()
      ?? `reset-${Date.now()}-${Math.random()}`;
    const pending = beginConditionalVisibilityReset(previous, {
      timerBlockId: selectedTimer.value ? timerId : null,
      policy: policy.value,
      previousTimerState,
      replacementTimerState,
      maximumResets: resetLimit.value,
      resetId,
      nowMs,
    });
    if (!pending) return;

    runtimeState.value = pending;
    await persistRuntimeState();
    if (selectedTimer.value && replacementTimerState) {
      await timerStateStorage.save(timerId, replacementTimerState);
    }
    const completedState = completeConditionalVisibilityReset(
      pending,
      resetId,
      policy.value,
    );
    runtimeState.value = completedState;
    try {
      await persistRuntimeState();
    } catch (error) {
      runtimeState.value = pending;
      throw error;
    }

    history.value = runtimeState.value.history;
    mountState.value = { mountId: resetId, counted: true };
    evaluation.value = evaluateVisibilityForMount(
      policy.value,
      history.value,
      mountState.value,
    );
    visible.value = true;
  } catch (error) {
    resetError.value = "The reset could not be completed. Please try again.";
    logger.error("Failed to reset conditional visibility content", error);
  } finally {
    resetBusy.value = false;
  }
}

onMounted(async () => {
  if (features.editor) {
    loading.value = false;
    visible.value = true;
    return;
  }

  const blockInitialization = () => {
    loading.value = false;
    stateReady.value = false;
  };
  const locator = conditionalVisibilityStateLocator(
    getResolvedDataPath(props.id),
    props.id,
  );
  let record: Awaited<ReturnType<typeof userStateStorage.load>> = null;

  try {
    record = await userStateStorage.load(locator);
  } catch (error) {
    const failOpen = handlePersistenceFailure({
      operation: "view history load",
      error,
      blockingMessage: "This content is temporarily unavailable because its view history could not be loaded. Please try again.",
    });
    if (!failOpen) {
      blockInitialization();
      return;
    }
  }

  runtimeState.value = normalizeConditionalVisibilityState(
    record?.value,
    policy.value,
  );

  if (!record) {
    try {
      const legacyAnswer = await getAnswer(
        props.id,
        getResolvedDataPath(props.id).join("/"),
      );
      if (legacyAnswer) {
        runtimeState.value = updateConditionalVisibilityState(
          runtimeState.value,
          {
            history: reconcileVisibilityHistory(
              legacyAnswer,
              policy.value,
            ),
          },
        );
        try {
          await persistRuntimeState();
        } catch (error) {
          const failOpen = handlePersistenceFailure({
            operation: "legacy view history migration",
            error,
            blockingMessage: "This content is temporarily unavailable because its existing view history could not be migrated.",
          });
          if (!failOpen) {
            blockInitialization();
            return;
          }
        }
      }
    } catch (error) {
      const failOpen = handlePersistenceFailure({
        operation: "legacy view history load",
        error,
        blockingMessage: "This content is temporarily unavailable because its existing view history could not be loaded.",
      });
      if (!failOpen) {
        blockInitialization();
        return;
      }
    }
  }

  history.value = runtimeState.value.history;

  try {
    if (await completePendingReset()) {
      loading.value = false;
      stateReady.value = true;
      return;
    }
  } catch (error) {
    const failOpen = handlePersistenceFailure({
      operation: "pending reset recovery",
      error,
      blockingMessage: "This content is temporarily unavailable because its pending reset could not be completed safely.",
    });
    if (!failOpen) {
      blockInitialization();
      return;
    }
    visible.value = true;
    mountState.value = {
      mountId: runtimeState.value.pendingResetId ?? props.id,
      counted: true,
    };
    updateEvaluation();
    loading.value = false;
    stateReady.value = true;
    return;
  }

  if (
    runtimeState.value.activeReveal
    && runtimeState.value.activeReveal.timerBlockId === configuredTimerId.value
  ) {
    let timerActive = false;
    try {
      timerActive = await selectedTimerIsActive();
    } catch (error) {
      const failOpen = handlePersistenceFailure({
        operation: "active reveal timer load",
        error,
        blockingMessage: "This content is temporarily unavailable because its active view timer could not be loaded.",
      });
      if (!failOpen) {
        blockInitialization();
        return;
      }
      timerActive = true;
    }
    if (timerActive) {
      visible.value = true;
      mountState.value = {
        mountId: runtimeState.value.activeReveal.id,
        counted: true,
      };
      updateEvaluation();
      loading.value = false;
      stateReady.value = true;
      return;
    }
  }

  if (runtimeState.value.activeReveal) {
    const previous = runtimeState.value;
    runtimeState.value = updateConditionalVisibilityState(
      previous,
      { activeReveal: null },
    );
    try {
      await persistRuntimeState();
    } catch (error) {
      const failOpen = handlePersistenceFailure({
        operation: "stale active reveal cleanup",
        error,
        blockingMessage: "This content is temporarily unavailable because its completed view state could not be updated.",
      });
      if (!failOpen) {
        runtimeState.value = previous;
        blockInitialization();
        return;
      }
    }
  }

  updateEvaluation();
  loading.value = false;

  if (selectedTimer.value) {
    try {
      if (!(await selectedTimerIsActive())) {
        stateReady.value = true;
        return;
      }
    } catch (error) {
      const failOpen = handlePersistenceFailure({
        operation: "timer availability load",
        error,
        blockingMessage: "This content is temporarily unavailable because its timer state could not be loaded.",
      });
      if (!failOpen) {
        blockInitialization();
        return;
      }
    }
  }

  await consumeView();
  stateReady.value = evaluation.value !== null
    && !blockingPersistenceError.value;
});

const selectedTimerCompleted = computed(() => {
  if (!selectedTimer.value) return false;
  const source = {
    type: "component-state" as const,
    blockId: configuredTimerId.value,
    field: "completed",
  };
  return conditionValues.isAvailable(source)
    && conditionValues.resolveValue(source) === true;
});

watch(selectedTimerCompleted, (completed) => {
  if (completed) {
    void closeActiveReveal();
  }
});

useComponentConditionState(
  () => props.id,
  () =>
    stateReady.value && !blockingPersistenceError.value
      ? {
          contentVisible: features.editor || visible.value,
          eligible: features.editor || evaluation.value?.eligible === true,
          resetCount: runtimeState.value?.resetCount ?? 0,
          resetsRemaining: remainingResets.value,
        }
      : {
          contentVisible: undefined,
          eligible: undefined,
          resetCount: undefined,
          resetsRemaining: undefined,
        },
);
</script>

<template>
  <div :class="$attrs.class">
    <div
      v-if="features.editor"
      class="relative min-h-10 rounded-md border border-dashed border-border/70 p-2"
    >
      <div class="pointer-events-none absolute right-2 top-1.5 z-10 flex items-center gap-1 rounded-sm bg-muted/90 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        <Icon name="lucide:eye" class="size-3" />
        {{ authorStatus }}
      </div>
      <slot />
    </div>

    <template v-else-if="visible">
      <slot />
    </template>

    <p
      v-else-if="blockingPersistenceError"
      role="alert"
      class="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
    >
      {{ blockingPersistenceError }}
    </p>

    <div
      v-else-if="!loading && stateReady && showHiddenState"
      class="space-y-3 rounded-md bg-card px-4 py-3 text-card-foreground"
    >
      <div
        v-if="activeHiddenMarkdown?.body"
        class="prose max-w-none text-sm text-foreground dark:prose-invert"
      >
        <MDCRenderer
          :body="activeHiddenMarkdown.body"
          :data="activeHiddenMarkdown.data"
          tag="div"
        />
      </div>
      <Button
        v-if="resetAvailable"
        variant="link"
        class="h-auto p-0 text-left text-sm"
        :disabled="resetBusy"
        @click="resetContent"
      >
        <Icon
          v-if="resetBusy"
          name="lucide:loader-circle"
          class="mr-1.5 size-3.5 animate-spin"
        />
        {{ resetLabel }}
      </Button>
      <p v-if="resetError" role="alert" class="text-xs text-destructive">
        {{ resetError }}
      </p>
    </div>

    <span v-else class="block min-h-px" aria-hidden="true" />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.CONDITIONAL_VISIBILITY,
  editor: () => import("#bb/components/editor/container/conditional-visibility.vue"),
  id: "conditional-visibility",
  icon: "lucide:eye",
  type: "Conditional Visibility",
  description: "Enforces per-user view limits and can keep a view active for a nested timer.",
  default: {
    policy: {
      composition: "all",
      triggers: [
        {
          id: "lifetime-default",
          type: "lifetime-maximum",
          maximum: 1,
        },
      ],
    },
    timerBlockId: "",
    hiddenMessageEnabled: false,
    hiddenMessage: "This content is no longer available.",
    resetEnabled: false,
    resetLimit: 1,
    resetLabel: "Request another attempt",
    resetExhaustedMessage: "No resets remain for this content.",
    persistenceFailureBehavior: "show-content",
    children: [],
  },
  categories: ["Containers"],
  order: 12006,
  conditionStateFields: [
    {
      key: "contentVisible",
      label: "Content visible",
      description: "Whether this container is currently displaying its content.",
      valueType: "boolean",
    },
    {
      key: "eligible",
      label: "Eligible",
      description: "Whether the current user is eligible to view this content.",
      valueType: "boolean",
    },
    {
      key: "resetCount",
      label: "Reset count",
      description: "Number of lifetime resets used for this container.",
      valueType: "number",
    },
    {
      key: "resetsRemaining",
      label: "Resets remaining",
      description: "Number of configured lifetime resets still available.",
      valueType: "number",
    },
  ],
};
</script>
